<script setup lang="ts">
const open = defineModel<boolean>("open", { required: true })

const { feature } = defineProps<{
  feature: UpsellFeature
}>()

const { featureConfig, featureTiers } = useFeatureAccess(feature)
const { $api } = useNuxtApp()
const { $trackEvent } = useNuxtApp()
const { workspaceId } = useAuth()
const { mutate: sendNotification, loading: sendNotificationLoading } =
  useMutation({
    successKey: "upsell.notify_success",
    fetchFunction: async () => {
      await $api("/admin/v1/workspace/request-feature", {
        method: "post",
        body: {
          feature: featureConfig.value.title,
        },
      })
    },
  })

function handleSendNotification() {
  $trackEvent("upsell_notify_clicked", {
    workspace_id: workspaceId.value,
    feature_name: featureConfig.value.title,
  })
  sendNotification()
}
</script>

<template>
  <Dialog v-model:open="open" class="max-w-xl p-0">
    <div class="relative overflow-hidden bg-white">
      <div class="absolute top-4 right-4 z-10 flex justify-end gap-3">
        <div
          class="inline-flex cursor-default items-center gap-1 rounded-full bg-white/80 py-1 pr-3 pl-2.5 text-[13px] font-medium text-indigo-600 shadow-xs ring-1 ring-indigo-100"
        >
          <SvgoPikaLock class="size-4" />
          <span class="leading-3">
            {{ featureTiers?.next_tier?.name ?? $t("upsell.locked_feature") }}
          </span>
        </div>
        <button
          type="button"
          class="inline-flex size-7 cursor-pointer items-center justify-center rounded-full bg-white/90 text-zinc-400 shadow-xs ring-1 ring-zinc-200 transition hover:bg-white hover:text-zinc-600"
          @click="open = false"
        >
          <SvgoPikaClose class="size-4" />
        </button>
      </div>

      <div
        class="relative overflow-hidden border-zinc-100 bg-linear-to-br from-violet-300/20 via-white to-zinc-50/70 pt-3 pb-6"
      >
        <svg
          class="stroke-dasharray-[none] stroke-dashoffset-[0] pointer-events-none absolute inset-0 z-0 h-full w-full"
          style="
            -webkit-mask-image: radial-gradient(
              ellipse at 60% 45%,
              #000 30%,
              transparent 100%
            );
            mask-image: radial-gradient(
              ellipse at 60% 45%,
              #000 30%,
              transparent 100%
            );
          "
          aria-hidden="true"
        >
          <pattern
            id="p-onboarding-dots"
            x="0"
            y="0"
            width="12"
            height="12"
            patternUnits="userSpaceOnUse"
          >
            <circle
              cx="6"
              cy="6"
              r="0.75"
              fill="#D9DDE3"
              class="stroke-dasharray-[none] stroke-dashoffset-[0]"
            />
          </pattern>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="url(#p-onboarding-dots)"
            class="stroke-dasharray-[none] stroke-dashoffset-[0]"
          />
        </svg>
        <div
          class="pointer-events-none absolute inset-x-0 -bottom-4 z-10 h-12 bg-linear-to-t from-white via-white/80 to-transparent"
        />
        <div class="relative z-50 mx-auto mt-6 w-4/8">
          <component :is="featureConfig.graphicComponent" />
        </div>
      </div>

      <div class="space-y-6 px-6 pb-6 sm:px-8">
        <div class="space-y-2">
          <TypographyHeading
            class="text-xl font-semibold tracking-tight text-zinc-950"
          >
            {{ featureConfig.title }}
          </TypographyHeading>
          <TypographyText>
            {{ featureConfig.summary }}
          </TypographyText>
        </div>

        <TransitionGroup
          name="upsell-items"
          class="space-y-3"
          tag="ul"
          appear
          enter-active-class="transition-all duration-700 ease-out"
          enter-from-class="translate-y-2 opacity-0"
          enter-to-class="translate-y-0 opacity-100"
        >
          <li
            v-for="(highlight, index) in featureConfig.highlights"
            :key="highlight"
            class="flex items-center gap-2 text-zinc-800"
            :style="{ transitionDelay: `${Number(index) * 100}ms` }"
          >
            <SvgoPikaCheckTickCircle class="size-4.5 text-emerald-500" />
            <TypographyText class="text-zinc-800">{{
              highlight
            }}</TypographyText>
          </li>
        </TransitionGroup>

        <div
          class="grid gap-3 pt-1"
          :class="{ 'sm:grid-cols-2': featureConfig.knowledgeBaseLink }"
        >
          <FormButton
            v-if="featureConfig.knowledgeBaseLink"
            :to="featureConfig.knowledgeBaseLink"
            target="_blank"
            variant="secondary"
            class="w-full justify-center"
          >
            <template #icon>
              <SvgoPikaBook />
            </template>
            {{ $t("upsell.knowledge_base") }}
          </FormButton>
          <FormButton
            :loading="sendNotificationLoading"
            variant="black"
            class="w-full justify-center bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500"
            @click="handleSendNotification"
          >
            <template #icon>
              <SvgoPikaBell />
            </template>
            {{ $t("upsell.contact") }}
          </FormButton>
        </div>
      </div>
    </div>
  </Dialog>
</template>
